import { type UserInvitationResult } from '../../../store/auth/auth'
import { type BulkCsvRow } from './bulkCsvRows'

export type BulkResultStatus = 'invited' | 'alreadyMember' | 'alreadyInvited' | 'requested' | 'invalid' | 'serverError'

export interface BulkResultRow {
  row: BulkCsvRow
  status: BulkResultStatus
  errors: string[]
  invitationId: string | null
}

export interface BulkInviteResult {
  rows: BulkResultRow[]
  sentCount: number
  alreadyMemberCount: number
  alreadyInvitedCount: number
  requestedCount: number
  requestedIds: string[]
  notAcceptedCount: number
  allSent: boolean
}

const _SERVER_STATUSES: Record<string, BulkResultStatus> = {
  invited: 'invited',
  already_member: 'alreadyMember',
  already_invited: 'alreadyInvited',
  requested: 'requested',
  invalid: 'invalid',
}

const _STATUS_ORDER: Record<BulkResultStatus, number> = {
  invited: 0,
  alreadyMember: 0,
  requested: 1,
  alreadyInvited: 2,
  invalid: 3,
  serverError: 3,
}

export const buildBulkResult = (rows: BulkResultRow[]): BulkInviteResult => {
  const sortedRows = [...rows].sort((a, b) => _STATUS_ORDER[a.status] - _STATUS_ORDER[b.status] || a.row.index - b.row.index)
  const requestedRows = rows.filter((resultRow) => resultRow.status === 'requested')
  const sentCount = rows.filter((resultRow) => resultRow.status === 'invited').length
  const alreadyMemberCount = rows.filter((resultRow) => resultRow.status === 'alreadyMember').length
  const alreadyInvitedCount = rows.filter((resultRow) => resultRow.status === 'alreadyInvited').length
  const notAcceptedCount = rows.filter((resultRow) => resultRow.status === 'invalid' || resultRow.status === 'serverError').length

  return {
    rows: sortedRows,
    sentCount,
    alreadyMemberCount,
    alreadyInvitedCount,
    requestedCount: requestedRows.length,
    requestedIds: requestedRows.map((resultRow) => resultRow.invitationId).filter((id): id is string => id !== null),
    notAcceptedCount,
    allSent: rows.length > 0 && sentCount === rows.length,
  }
}

export const getBulkInviteResult = (submittedRows: BulkCsvRow[], responseUsers: UserInvitationResult[]): BulkInviteResult => {
  const responseByEmail = new Map(responseUsers.map((user) => [user.email.toLowerCase(), user]))

  const rows = submittedRows.map((row): BulkResultRow => {
    const response = responseByEmail.get(row.email.toLowerCase())
    if (!response) {
      return { row, status: 'serverError', errors: [], invitationId: null }
    }
    return {
      row,
      status: _SERVER_STATUSES[response.status] ?? 'serverError',
      errors: response.errors ?? [],
      invitationId: response.invitationId ?? null,
    }
  })

  return buildBulkResult(rows)
}
